import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useProposalDetails } from '@/features/quotes/hooks/homeowner/use-proposal-details.hook';
import type { CapitalGlobalCoverageCatalogItem } from '@/features/quotes/schemas/capital-global/coverages-catalog';
import {
  findBenefitOption,
  isGroupedAssistanceBenefit,
  isStandaloneAssistanceBenefit,
} from '@/features/quotes/schemas/capital-global/coverages-catalog';
import type {
  CoverageAttach,
  CoverageProfileEntry,
  CoverageProfileGroupEntry,
  ProposalDetails,
  RiskEntityPayload,
} from '@/services/quotes/types';
import type { CoverageUmbrella } from '@/services/coverages/types';
import type {
  CoveragesDataMap,
  CoveragesEntityValues,
} from '@/features/quotes/components/capital-global/types';
import { deriveGroupCoverages } from '@/features/quotes/components/capital-global/steps/utils/derive-group-coverages.util';
import {
  getGroupReferenceCapitals,
  isUniformeColaboradoresOnly,
} from '@/features/quotes/components/capital-global/steps/utils/map-api.util';
import {
  mapAttachValidationToCoverageErrors,
  type MapAttachValidationToCoverageErrorsResult,
} from '@/features/quotes/utils/map-attach-validation-to-coverage-errors.util';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { removeFormatRegisterNumber } from '@/shared/utils/remove-format-register-number';
import { toaster } from '@/shared/ui/toaster.component';
import { useUpdateCapitalGlobalQuote } from './use-update-capital-global-quote.hook';

const COVERAGES_STEP_CODE = 'COVERAGES_GLOBAL';

type CapitalGlobalBenefitAttach = {
  code: string;
  productElementId: number;
  value: number;
};

export type CapitalGlobalCoverageAttach = {
  code: string;
  productElementId: number;
  value: number;
  benefits?: CapitalGlobalBenefitAttach[];
};

type Params = {
  onNext: () => void;
  getCoveragesMap: () => CoveragesDataMap;
  coverageCatalog: CapitalGlobalCoverageCatalogItem[];
  assistanceUmbrella: CoverageUmbrella | null;
  capitalizationUmbrella: CoverageUmbrella | null;
  morteId: string | undefined;
  onAttachValidationError?: (
    result: MapAttachValidationToCoverageErrorsResult
  ) => void;
};

function buildUmbrellaCoverages(
  entityValues: CoveragesEntityValues,
  assistanceUmbrella: CoverageUmbrella | null,
  capitalizationUmbrella: CoverageUmbrella | null
): CapitalGlobalCoverageAttach[] {
  const umbrellaAssistanceItems: CapitalGlobalCoverageAttach[] = (() => {
    if (!assistanceUmbrella) return [];
    const selectedBenefits = assistanceUmbrella.benefits.filter(b => {
      if (isStandaloneAssistanceBenefit(b)) {
        return entityValues.assistances[b.code]?.enabled;
      }
      if (isGroupedAssistanceBenefit(b)) {
        return b.code === entityValues.assistanceFuneral;
      }
      return false;
    });
    if (selectedBenefits.length === 0) return [];
    return [
      {
        code: assistanceUmbrella.code,
        productElementId: Number(assistanceUmbrella.id),
        value: 0,
        benefits: selectedBenefits.map(b => ({
          code: b.code,
          productElementId: Number(b.id),
          value: 0,
        })),
      },
    ];
  })();

  const umbrellaCapitalizationItems: CapitalGlobalCoverageAttach[] = (() => {
    if (!capitalizationUmbrella || !entityValues.capitalization) return [];
    const selectedBenefit = capitalizationUmbrella.benefits.find(
      b => b.code === entityValues.capitalization
    );
    if (!selectedBenefit) return [];
    return [
      {
        code: capitalizationUmbrella.code,
        productElementId: Number(capitalizationUmbrella.id),
        value: 0,
        benefits: [
          {
            code: selectedBenefit.code,
            productElementId: Number(selectedBenefit.id),
            value: 0,
          },
        ],
      },
    ];
  })();

  return [...umbrellaAssistanceItems, ...umbrellaCapitalizationItems];
}

/**
 * Benefício escolhido na linha → `benefits` da cobertura no attach, mesmo
 * formato que o guarda-chuva de Assistência/Capitalização já envia (o valor
 * fica na cobertura; o benefício vai com `value: 0`).
 */
function buildCoverageBenefitsAttach(
  item: CapitalGlobalCoverageCatalogItem,
  benefitCode: string | undefined
): Pick<CoverageAttach, 'benefits'> {
  const option = findBenefitOption(item, benefitCode);
  if (!option) return {};

  return {
    benefits: [
      {
        code: option.code,
        value: 0,
        ...(option.productElementId != null
          ? { productElementId: String(option.productElementId) }
          : {}),
      },
    ],
  };
}

function buildLeafCoveragesFromDerived(
  derived: ReturnType<typeof deriveGroupCoverages>,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  entityValues: CoveragesEntityValues
): CoverageAttach[] {
  return coverageCatalog
    .filter(c => {
      const row = derived[c.id];
      if (!row?.enabled) return false;
      // Capital 0 é legítimo quando a opção de benefício não tem valor em R$
      // (ex.: "30 Diárias") — quem contrata a cobertura é o benefício.
      if (row.capital > 0) return true;
      return Boolean(
        findBenefitOption(c, entityValues.coverages[c.id]?.benefitCode)
      );
    })
    .map(c => ({
      code: c.code,
      productElementId: String(c.productElementId),
      value: derived[c.id].capital,
      ...buildCoverageBenefitsAttach(
        c,
        entityValues.coverages[c.id]?.benefitCode
      ),
    }));
}

function buildUmbrellaCoveragesAsAttach(
  entityValues: CoveragesEntityValues,
  assistanceUmbrella: CoverageUmbrella | null,
  capitalizationUmbrella: CoverageUmbrella | null
): CoverageAttach[] {
  return buildUmbrellaCoverages(
    entityValues,
    assistanceUmbrella,
    capitalizationUmbrella
  ).map(item => ({
    code: item.code,
    value: item.value,
    productElementId: String(item.productElementId),
    benefits: item.benefits?.map(b => ({
      code: b.code,
      value: b.value,
      productElementId: String(b.productElementId),
    })),
  }));
}

/**
 * Builds the list of coverages to attach for a single entity (uniforme).
 */
function buildEntityCoveragePayload(
  entityValues: CoveragesEntityValues,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  assistanceUmbrella: CoverageUmbrella | null,
  capitalizationUmbrella: CoverageUmbrella | null
): CoverageAttach[] {
  const leafCoverages: CoverageAttach[] = coverageCatalog
    .filter(c => entityValues.coverages[c.id]?.enabled)
    .map(c => {
      const row = entityValues.coverages[c.id];
      const capital = parseCurrencyToNumber(row.maxIndividualCapital);
      return {
        code: c.code,
        productElementId: String(c.productElementId),
        value: Number.isFinite(capital) ? capital : 0,
        ...buildCoverageBenefitsAttach(c, row.benefitCode),
      };
    });

  return [
    ...leafCoverages,
    ...buildUmbrellaCoveragesAsAttach(
      entityValues,
      assistanceUmbrella,
      capitalizationUmbrella
    ),
  ];
}

function buildEscalonadoGroupsPayload(
  entityValues: CoveragesEntityValues,
  riskProfile: RiskEntityPayload,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  assistanceUmbrella: CoverageUmbrella | null,
  capitalizationUmbrella: CoverageUmbrella | null,
  morteId: string | undefined
): CoverageProfileGroupEntry[] {
  const groupCapitals = getGroupReferenceCapitals(riskProfile);
  if (!groupCapitals) return [];

  const umbrellas = buildUmbrellaCoveragesAsAttach(
    entityValues,
    assistanceUmbrella,
    capitalizationUmbrella
  );

  const buildGroup = (
    memberCategoryCode: '1' | '2',
    groupReferenceCapital: number
  ): CoverageProfileGroupEntry => {
    const derived = deriveGroupCoverages({
      rows: entityValues.coverages,
      morteId,
      groupReferenceCapital,
      largestReferenceCapital: groupCapitals.largest,
      coverageCatalog,
    });

    return {
      memberCategoryCode,
      coverages: [
        ...buildLeafCoveragesFromDerived(
          derived,
          coverageCatalog,
          entityValues
        ),
        ...umbrellas,
      ],
    };
  };

  return [
    buildGroup('2', groupCapitals.partnersAndDirectors),
    buildGroup('1', groupCapitals.employees),
  ];
}

function parseCurrencyToNumber(raw: string): number {
  return parseInt(String(raw).replace(/\D/g, ''), 10) / 100;
}

function resolveCustomerDocumentNumber(customer: {
  cpf?: string;
  idNo?: string;
  documentNumber?: string;
}): string {
  // Identidade = `documentNumber` (o CAEPF), que é como as entidades e o
  // `coveragesMap` são chaveados. Pós-split o `cpf` guarda o CPF derivado (valor
  // diferente do CAEPF); usá-lo primeiro faz o lookup do coveragesMap falhar e a
  // entidade sair "sem coberturas".
  const candidates = [
    customer.documentNumber ?? '',
    customer.idNo ?? '',
    customer.cpf ?? '',
  ];
  for (const raw of candidates) {
    const clean = removeFormatRegisterNumber(raw);
    if (clean) return clean;
  }
  return '';
}

function findRiskProfileForDocument(
  proposalDetails: ProposalDetails | undefined,
  documentNumber: string
): RiskEntityPayload | undefined {
  const cleanTarget = removeFormatRegisterNumber(documentNumber);
  return proposalDetails?.riskProfileList?.find(
    p => removeFormatRegisterNumber(p.documentNumber) === cleanTarget
  );
}

function entryHasCoverages(entry: CoverageProfileEntry): boolean {
  if (entry.groups?.some(group => group.coverages.length > 0)) {
    return true;
  }
  return (entry.coverages?.length ?? 0) > 0;
}

function buildCoverageProfileList(
  proposalDetails: ProposalDetails | undefined,
  coveragesMap: CoveragesDataMap,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  assistanceUmbrella: CoverageUmbrella | null,
  capitalizationUmbrella: CoverageUmbrella | null,
  morteId: string | undefined
): CoverageProfileEntry[] {
  if (!proposalDetails) return [];

  const stipulantDoc = resolveCustomerDocumentNumber(proposalDetails.customer);

  const subDocs = (proposalDetails.customers ?? [])
    .filter(c => c.sequenceNumber !== undefined && c.sequenceNumber !== 0)
    .sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0))
    .map(c => resolveCustomerDocumentNumber(c))
    .filter(Boolean);

  const allDocs = stipulantDoc ? [stipulantDoc, ...subDocs] : subDocs;

  return allDocs.map(documentNumber => {
    const entityValues = coveragesMap[documentNumber];
    const riskProfile = findRiskProfileForDocument(
      proposalDetails,
      documentNumber
    );

    if (!entityValues) {
      return { documentNumber, coverages: [] };
    }

    // Filtro de cobertura exclusiva de sócio resolvido pelo perfil de risco
    // DESTE estipulante. Usar o catálogo já filtrado pela aba ativa descartava
    // silenciosamente as coberturas de sócio dos demais estipulantes.
    const colaboradoresOnly = isUniformeColaboradoresOnly(riskProfile);
    const entityCoverageCatalog = colaboradoresOnly
      ? coverageCatalog.filter(item => !item.partnerOnly)
      : coverageCatalog;
    // Capitalização exige sócios: sem umbrella, uma escolha antiga (cotação
    // salva antes da regra) não segue no payload e a Api não a recusa.
    const entityCapitalizationUmbrella = colaboradoresOnly
      ? null
      : capitalizationUmbrella;

    if (riskProfile?.groupProfile === 'ESCALONADO') {
      const groups = buildEscalonadoGroupsPayload(
        entityValues,
        riskProfile,
        entityCoverageCatalog,
        assistanceUmbrella,
        capitalizationUmbrella,
        morteId
      );
      return { documentNumber, groups };
    }

    const coverages = buildEntityCoveragePayload(
      entityValues,
      entityCoverageCatalog,
      assistanceUmbrella,
      entityCapitalizationUmbrella
    );

    return { documentNumber, coverages };
  });
}

export function useSubmitCapitalGlobalCoverages({
  onNext,
  getCoveragesMap,
  coverageCatalog,
  assistanceUmbrella,
  capitalizationUmbrella,
  morteId,
  onAttachValidationError,
}: Params) {
  const { policyId, id } = useQuoteParams();
  const resolvedPolicyId = (policyId || id || '').trim();

  const { data: proposalDetails } = useProposalDetails(
    resolvedPolicyId || undefined
  );
  const { mutateAsync, isPending } = useUpdateCapitalGlobalQuote();

  async function handleSubmit() {
    if (!resolvedPolicyId) {
      toaster.create({
        type: 'error',
        title: 'Dados incompletos',
        description: 'ID da cotação não encontrado. Volte e tente novamente.',
      });
      return;
    }

    const coveragesMap = getCoveragesMap();
    const coverageProfileList = buildCoverageProfileList(
      proposalDetails,
      coveragesMap,
      coverageCatalog,
      assistanceUmbrella,
      capitalizationUmbrella,
      morteId
    );

    if (coverageProfileList.length === 0) {
      toaster.create({
        type: 'error',
        title: 'Dados incompletos',
        description:
          'Não foi possível identificar os estipulantes. Recarregue a página e tente novamente.',
      });
      return;
    }

    const hasMissingEntityCoverages = coverageProfileList.some(
      entry => !entryHasCoverages(entry)
    );
    if (hasMissingEntityCoverages) {
      toaster.create({
        type: 'error',
        title: 'Dados incompletos',
        description:
          'Preencha as coberturas de todos os estipulantes antes de continuar.',
      });
      return;
    }

    try {
      const result = await mutateAsync({
        id: resolvedPolicyId,
        payload: {
          step: COVERAGES_STEP_CODE,
          coverageProfileList,
        },
      });

      if (result.success) onNext();
    } catch (error) {
      if (error instanceof ValidationError) {
        const mapped = mapAttachValidationToCoverageErrors(
          error,
          coverageCatalog.map(item => ({
            id: item.id,
            code: item.code,
            title: item.title,
          }))
        );
        onAttachValidationError?.(mapped);

        const description =
          mapped.coverageNames.length > 0
            ? `Erro nas coberturas: ${mapped.coverageNames.join(', ')}`
            : mapped.generalMessage?.trim() ||
              error.detail?.trim() ||
              (error.messages.length
                ? error.messages.join('\n')
                : error.message);

        toaster.create({
          type: 'error',
          title: 'Erro de validação',
          description,
        });
        return;
      }

      toaster.create({
        type: 'error',
        title: 'Não foi possível salvar',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  }

  return { handleSubmit, isPending };
}
